import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Upload, ClipboardText, Sparkle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ScriptUploader = ({ projectId, project, onScriptProcessed }) => {
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pastedScript, setPastedScript] = useState('');
  const [pacing, setPacing] = useState(project?.pacing || 'balanced');
  const [style, setStyle] = useState(project?.style || 'cinematic');
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['.txt', '.docx', '.pdf'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error('Please upload a TXT, DOCX, or PDF file');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('pacing', pacing);
      formData.append('style', style);

      const res = await axios.post(`${API}/projects/${projectId}/upload-script`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onScriptProcessed(res.data.scenes, res.data.total_duration);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process script');
    } finally {
      setProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePasteSubmit = async () => {
    if (!pastedScript.trim()) {
      toast.error('Please paste your script');
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/process-script`, {
        script: pastedScript,
        pacing,
        style
      });
      onScriptProcessed(res.data.scenes, res.data.total_duration);
      setShowPasteModal(false);
      setPastedScript('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process script');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
        <FileText size={16} />
        <span>Upload Script</span>
      </div>

      {/* Quick Settings */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Select value={pacing} onValueChange={setPacing}>
          <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white text-xs h-8" data-testid="pacing-select">
            <SelectValue placeholder="Pacing" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#333333]">
            <SelectItem value="slower">Slower</SelectItem>
            <SelectItem value="balanced">Balanced</SelectItem>
            <SelectItem value="faster">Faster</SelectItem>
          </SelectContent>
        </Select>
        <Select value={style} onValueChange={setStyle}>
          <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white text-xs h-8" data-testid="style-select">
            <SelectValue placeholder="Style" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#333333]">
            <SelectItem value="cinematic">Cinematic</SelectItem>
            <SelectItem value="documentary">Documentary</SelectItem>
            <SelectItem value="realistic">Realistic</SelectItem>
            <SelectItem value="vintage">Vintage</SelectItem>
            <SelectItem value="dramatic">Dramatic</SelectItem>
            <SelectItem value="modern">Modern</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Upload Buttons */}
      <div className="flex gap-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".txt,.docx,.pdf"
          className="hidden"
          data-testid="script-file-input"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={processing}
          className="flex-1 border-[#333333] text-white hover:bg-[#262626] gap-2"
          data-testid="upload-script-btn"
        >
          <Upload size={16} />
          Upload
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPasteModal(true)}
          disabled={processing}
          className="flex-1 border-[#333333] text-white hover:bg-[#262626] gap-2"
          data-testid="paste-script-btn"
        >
          <ClipboardText size={16} />
          Paste
        </Button>
      </div>

      {processing && (
        <div className="flex items-center gap-2 text-[#FF5D01] text-sm">
          <Sparkle size={16} className="animate-pulse" />
          Processing script...
        </div>
      )}

      {/* Paste Modal */}
      <Dialog open={showPasteModal} onOpenChange={setShowPasteModal}>
        <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Paste Your Script</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Paste your full YouTube script here... Use paragraph breaks to separate scenes."
              value={pastedScript}
              onChange={(e) => setPastedScript(e.target.value)}
              className="bg-[#0a0a0a] border-[#333333] text-white min-h-[300px] font-mono text-sm"
              data-testid="paste-script-textarea"
              autoFocus
            />
            <p className="text-xs text-[#A1A1AA] mt-2">
              Tip: Use paragraph breaks to separate scenes. The AI will automatically detect natural scene breaks.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPasteModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button
              onClick={handlePasteSubmit}
              disabled={processing || !pastedScript.trim()}
              className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
              data-testid="process-script-btn"
            >
              <Sparkle size={16} />
              {processing ? 'Processing...' : 'Process Script'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
